import { evaluateCircle } from './gesture.js'

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)
const clamp01 = (value) => Math.max(0, Math.min(1, value))

export const SIGIL_SPELLS = Object.freeze({
  triangle: Object.freeze({
    shape: 'triangle', glyph: '△', element: 'fire', name: '點燃', vfx: 'flame-burst',
    audio: Object.freeze({ profile: 'rising-ignition', oscillator: 'sawtooth', envelope: 'fast-rise', noise: 'crackle', filter: 'highpass' }),
  }),
  square: Object.freeze({
    shape: 'square', glyph: '□', element: 'earth', name: '岩裂', vfx: 'rock-shatter',
    audio: Object.freeze({ profile: 'low-impact', oscillator: 'sine', envelope: 'impact-decay', noise: 'rock-grit', filter: 'lowpass' }),
  }),
  wave: Object.freeze({
    shape: 'wave', glyph: '≈', element: 'water', name: '潮汐', vfx: 'tidal-surge',
    audio: Object.freeze({ profile: 'soft-fall-drops', oscillator: 'sine', envelope: 'soft-fall', noise: 'droplets', filter: 'bandpass' }),
  }),
  spiral: Object.freeze({
    shape: 'spiral', glyph: '◎', element: 'wind', name: '風切', vfx: 'air-vortex',
    audio: Object.freeze({ profile: 'bright-chime', oscillator: 'triangle', envelope: 'bell-decay', noise: 'air-swish', filter: 'highpass' }),
  }),
  heart: Object.freeze({
    shape: 'heart', glyph: '♥', element: 'love', name: '心光術', vfx: 'rose-heart-bloom',
    audio: Object.freeze({ profile: 'double-heartbeat-glass', oscillator: 'sine', envelope: 'double-pulse', noise: 'warm-glass', filter: 'lowpass' }),
  }),
})

function bounds(points) {
  const xs = points.map((point) => point.x)
  const ys = points.map((point) => point.y)
  const minX = Math.min(...xs); const maxX = Math.max(...xs)
  const minY = Math.min(...ys); const maxY = Math.max(...ys)
  return { minX, maxX, minY, maxY, width: maxX - minX, height: maxY - minY, diagonal: Math.hypot(maxX - minX, maxY - minY) }
}

function perpendicularDistance(point, start, end) {
  const dx = end.x - start.x; const dy = end.y - start.y
  if (dx === 0 && dy === 0) return distance(point, start)
  return Math.abs(dy * point.x - dx * point.y + end.x * start.y - end.y * start.x) / Math.hypot(dx, dy)
}

function simplify(points, epsilon) {
  if (points.length <= 2) return points
  let maxDistance = 0; let split = 0
  for (let index = 1; index < points.length - 1; index += 1) {
    const value = perpendicularDistance(points[index], points[0], points.at(-1))
    if (value > maxDistance) { maxDistance = value; split = index }
  }
  if (maxDistance <= epsilon) return [points[0], points.at(-1)]
  return [...simplify(points.slice(0, split + 1), epsilon).slice(0, -1), ...simplify(points.slice(split), epsilon)]
}

function closedVertexCount(points, diagonal) {
  const ring = [...points]
  if (distance(ring[0], ring.at(-1)) < diagonal * 0.14) ring.pop()
  let first = 0; let second = 1; let max = 0
  for (let a = 0; a < ring.length; a += 1) {
    for (let b = a + 1; b < ring.length; b += 1) {
      const value = distance(ring[a], ring[b])
      if (value > max) { max = value; first = a; second = b }
    }
  }
  const ordered = first < second ? ring : [...ring.slice(first), ...ring.slice(0, first)]
  const split = first < second ? second - first : (second - first + ring.length) % ring.length
  const arcA = ordered.slice(0, split + 1)
  const arcB = [...ordered.slice(split), ...ordered.slice(0, 1)]
  const vertices = [
    ...simplify(arcA, diagonal * 0.075).slice(0, -1),
    ...simplify(arcB, diagonal * 0.075).slice(0, -1),
  ]
  let changed = true
  while (changed && vertices.length > 3) {
    changed = false
    for (let index = 0; index < vertices.length; index += 1) {
      const previous = vertices[(index - 1 + vertices.length) % vertices.length]
      const current = vertices[index]
      const next = vertices[(index + 1) % vertices.length]
      const incoming = { x: current.x - previous.x, y: current.y - previous.y }
      const outgoing = { x: next.x - current.x, y: next.y - current.y }
      const denominator = Math.max(Math.hypot(incoming.x, incoming.y) * Math.hypot(outgoing.x, outgoing.y), 1e-9)
      const turn = Math.acos(Math.max(-1, Math.min(1, (incoming.x * outgoing.x + incoming.y * outgoing.y) / denominator)))
      if (turn < 0.6) {
        vertices.splice(index, 1)
        changed = true
        break
      }
    }
  }
  return vertices.length
}

function closedCircularity(points) {
  if (points.length < 3) return 0
  let twiceArea = 0
  let perimeter = 0
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]
    const next = points[(index + 1) % points.length]
    twiceArea += current.x * next.y - next.x * current.y
    perimeter += distance(current, next)
  }
  const area = Math.abs(twiceArea) / 2
  return 4 * Math.PI * area / Math.max(perimeter * perimeter, 1e-9)
}

function crossingCount(values, center) {
  let crossings = 0
  let prior = Math.sign(values[0] - center)
  for (let index = 1; index < values.length; index += 1) {
    const current = Math.sign(values[index] - center)
    if (current && prior && current !== prior) crossings += 1
    if (current) prior = current
  }
  return crossings
}

function monotonicRatio(values) {
  const direction = Math.sign(values.at(-1) - values[0]) || 1
  let matching = 0; let meaningful = 0
  for (let index = 1; index < values.length; index += 1) {
    const delta = values[index] - values[index - 1]
    if (Math.abs(delta) < 0.5) continue
    meaningful += 1
    if (Math.sign(delta) === direction) matching += 1
  }
  return matching / Math.max(meaningful, 1)
}

function correlation(values) {
  const count = values.length
  const meanX = (count - 1) / 2
  const meanY = values.reduce((sum, value) => sum + value, 0) / count
  let numerator = 0; let denominatorX = 0; let denominatorY = 0
  values.forEach((value, index) => {
    const dx = index - meanX; const dy = value - meanY
    numerator += dx * dy; denominatorX += dx * dx; denominatorY += dy * dy
  })
  return numerator / Math.sqrt(Math.max(denominatorX * denominatorY, 1e-9))
}

function spiralMetrics(points, center) {
  const usable = points.filter((point) => distance(point, center) > 2)
  if (usable.length < 12) return { turns: 0, radialTrend: 0 }
  const angles = usable.map((point) => Math.atan2(point.y - center.y, point.x - center.x))
  let total = 0
  for (let index = 1; index < angles.length; index += 1) {
    let delta = angles[index] - angles[index - 1]
    while (delta > Math.PI) delta -= Math.PI * 2
    while (delta < -Math.PI) delta += Math.PI * 2
    total += delta
  }
  const radii = usable.map((point) => distance(point, center))
  return { turns: Math.abs(total) / (Math.PI * 2), radialTrend: Math.abs(correlation(radii)) }
}

function heartConfidence(points, box, closure) {
  if (closure > 0.22) return 0
  const centerX = (box.minX + box.maxX) / 2
  const central = points.filter((point) => Math.abs(point.x - centerX) <= box.width * 0.1)
  const leftTop = points.filter((point) => point.x < centerX - box.width * 0.14)
  const rightTop = points.filter((point) => point.x > centerX + box.width * 0.14)
  if (!central.length || !leftTop.length || !rightTop.length) return 0
  const centralTop = Math.min(...central.map((point) => point.y))
  const leftMin = Math.min(...leftTop.map((point) => point.y))
  const rightMin = Math.min(...rightTop.map((point) => point.y))
  const lobeTop = Math.min(leftMin, rightMin)
  const notchDepth = (centralTop - lobeTop) / Math.max(box.height, 1)
  const bottomBand = points.filter((point) => point.y >= box.maxY - box.height * 0.13)
  const bottomWidth = bottomBand.length ? (Math.max(...bottomBand.map((point) => point.x)) - Math.min(...bottomBand.map((point) => point.x))) / box.width : 1
  const bottom = points.reduce((best, point) => point.y > best.y ? point : best, points[0])
  const centeredPoint = Math.abs(bottom.x - centerX) / box.width
  const balancedLobes = Math.abs(leftMin - rightMin) / box.height
  const aspect = box.width / box.height
  if (notchDepth < 0.095 || bottomWidth > 0.34 || centeredPoint > 0.2 || balancedLobes > 0.2 || aspect < 0.68 || aspect > 1.55) return 0
  return clamp01(0.76 + Math.min(0.1, (notchDepth - 0.095) * 0.5) + Math.min(0.08, (0.34 - bottomWidth) * 0.3) - closure * 0.25)
}

export function classifySigil(points, { minConfidence = 0.72, allowClosedWindLoop = false } = {}) {
  const rejected = (shape = 'unknown', confidence = 0, reason = '請重畫中央符號') => ({ ok: false, shape, element: null, confidence, reason })
  if (!Array.isArray(points) || points.length < 10) return rejected('unknown', 0, '符號再畫完整一點')
  const box = bounds(points)
  if (box.diagonal < 36 || box.width < 8 || box.height < 8) return rejected('unknown', 0, '符號再畫大一點')
  const closure = distance(points[0], points.at(-1)) / box.diagonal
  const heart = heartConfidence(points, box, closure)
  if (heart >= minConfidence) return { ok: true, shape: 'heart', element: 'love', confidence: heart, reason: '符號成立' }

  if (closure <= 0.36) {
    const vertices = closedVertexCount(points, box.diagonal)
    const circularity = closedCircularity(points)
    if (allowClosedWindLoop && closure <= 0.2 && circularity >= 0.88) {
      const confidence = clamp01(circularity - closure * 0.25)
      if (confidence >= minConfidence) return { ok: true, shape: 'spiral', element: 'wind', confidence, reason: '同心風環成立', variant: 'loop' }
    }
    const shape = vertices === 3 ? 'triangle' : vertices === 4 && circularity <= 0.81 ? 'square' : 'unknown'
    if (vertices === 4 && circularity > 0.81) return rejected('circle', circularity, '圓形內符不會施放土元素')
    if (shape !== 'unknown') {
      const confidence = clamp01(1 - closure * 0.7 - Math.abs(box.width / box.height - 1) * (shape === 'square' ? 0.12 : 0.05))
      if (confidence >= minConfidence) return { ok: true, shape, element: SIGIL_SPELLS[shape].element, confidence, reason: '符號成立' }
      return rejected(shape, confidence)
    }
  }

  const xs = points.map((point) => point.x)
  const ys = points.map((point) => point.y)
  const aspect = box.width / Math.max(box.height, 1)
  const crossings = crossingCount(ys, (box.minY + box.maxY) / 2)
  const xMonotonic = monotonicRatio(xs)
  if (closure > 0.35 && aspect >= 1.8 && crossings >= 3 && xMonotonic >= 0.76) {
    const confidence = clamp01(0.76 + Math.min(0.14, (aspect - 2) * 0.06) + Math.min(0.1, (crossings - 3) * 0.03) + (xMonotonic - 0.76) * 0.2)
    if (confidence >= minConfidence) return { ok: true, shape: 'wave', element: 'water', confidence, reason: '符號成立' }
  }

  const candidates = [spiralMetrics(points, points[0]), spiralMetrics([...points].reverse(), points.at(-1))]
  const spiral = candidates.sort((a, b) => (b.turns * b.radialTrend) - (a.turns * a.radialTrend))[0]
  if (closure > 0.22 && spiral.turns >= 0.95 && spiral.radialTrend >= 0.72) {
    const confidence = clamp01(0.72 + Math.min(0.16, (spiral.turns - 1.45) * 0.08) + (spiral.radialTrend - 0.72) * 0.25)
    if (confidence >= minConfidence) return { ok: true, shape: 'spiral', element: 'wind', confidence, reason: '符號成立' }
  }

  return rejected('unknown', 0.35, '看不清是哪一種符號，只重畫中央符號')
}

export function classifySigilStream(points, { openStartIndex = 0, ...classifierOptions } = {}) {
  if (!Array.isArray(points) || points.length < 10) return { ...classifySigil(points, classifierOptions), startIndex: 0 }
  const boundedOpenStart = Math.max(0, Math.min(openStartIndex, points.length - 10))
  const directPoints = points.slice(boundedOpenStart)
  const direct = classifySigil(directPoints, classifierOptions)
  const openShapeMature = !['wave', 'spiral'].includes(direct.shape) || directPoints.length >= 60
  let best = openShapeMature
    ? { ...direct, startIndex: boundedOpenStart }
    : { ...direct, ok: false, element: null, reason: '符號再畫完整一點', startIndex: boundedOpenStart }
  const candidateStarts = new Set([0])
  const floor = Math.max(0, points.length - 220)
  const last = points.at(-1)

  // A real one-stroke cast often carries a smooth bridge tail into a closed glyph.
  // Search recent suffixes whose own start closes against the current endpoint.
  for (let index = floor; index <= points.length - 10; index += 1) {
    const suffix = points.slice(index)
    const box = bounds(suffix)
    if (box.diagonal >= 36 && distance(suffix[0], last) / box.diagonal <= 0.26) candidateStarts.add(index)
  }

  for (const startIndex of candidateStarts) {
    if (startIndex === boundedOpenStart) continue
    const result = classifySigil(points.slice(startIndex), classifierOptions)
    if (result.ok && ['triangle', 'square', 'heart'].includes(result.shape)
      && (!best.ok || result.confidence > best.confidence)) best = { ...result, startIndex }
  }
  return best
}

export function classifyHeartStream(points, classifierOptions = {}) {
  const rejected = { ok: false, shape: 'unknown', element: null, confidence: 0, reason: '愛心再畫完整一點', startIndex: 0 }
  if (!Array.isArray(points) || points.length < 10) return rejected
  const floor = Math.max(0, points.length - 220)
  const last = points.at(-1)
  let best = rejected
  for (let startIndex = floor; startIndex <= points.length - 10; startIndex += 1) {
    const suffix = points.slice(startIndex)
    const box = bounds(suffix)
    if (box.diagonal < 36 || distance(suffix[0], last) / box.diagonal > 0.26) continue
    const result = classifySigil(suffix, classifierOptions)
    if (result.ok && result.shape === 'heart' && (!best.ok || result.confidence > best.confidence)) {
      best = { ...result, startIndex }
    }
  }
  return best
}

export function analyzeWholeStroke(points, {
  viewportWidth = 390,
  viewportHeight = 844,
  centralZoneRatio = 0.72,
} = {}) {
  const rejected = (reason, details = {}) => ({
    ok: false,
    reason,
    outer: null,
    classification: null,
    outerPoints: [],
    bridgePoints: [],
    centralPoints: [],
    ...details,
  })
  if (!Array.isArray(points) || points.length < 34) return rejected('整筆軌跡再畫完整一點')

  let outerCandidate = null
  const latestOuterEnd = points.length - 10
  for (let endIndex = 24; endIndex <= latestOuterEnd; endIndex += 1) {
    const outerPoints = points.slice(0, endIndex)
    const evaluation = evaluateCircle(outerPoints, viewportWidth, viewportHeight)
    if (!evaluation.ok) continue
    const remainder = points.slice(endIndex)
    const inwardDistance = Math.min(...remainder.map((point) => distance(point, evaluation.center)))
    if (inwardDistance > evaluation.radius * 0.82) continue
    const inwardScore = clamp01(1 - inwardDistance / Math.max(evaluation.radius, 1)) * 14
    const rank = evaluation.score + inwardScore
    if (!outerCandidate || rank > outerCandidate.rank) {
      outerCandidate = { endIndex, evaluation, rank }
    }
  }
  if (!outerCandidate) return rejected('外圈與完整內符需要在同一筆中完成')

  const { endIndex: outerEndIndex, evaluation: outer } = outerCandidate
  const entryIndex = points.findIndex((point, index) => (
    index >= outerEndIndex && distance(point, outer.center) <= outer.radius * centralZoneRatio
  ))
  if (entryIndex < 0) return rejected('畫完外圈後，讓同一筆進入圓心再畫符號', { outer })

  let glyphCandidate = null
  const firstStart = outerEndIndex
  for (let startIndex = firstStart; startIndex <= points.length - 10; startIndex += 1) {
    const centralPoints = points.slice(startIndex)
    const insideRatio = centralPoints.filter((point) => distance(point, outer.center) <= outer.radius * 0.98).length / centralPoints.length
    if (insideRatio < centralZoneRatio) continue
    const classification = classifySigil(centralPoints, { allowClosedWindLoop: true })
    if (!classification.ok) continue
    const retainedRatio = centralPoints.length / Math.max(points.length - entryIndex, 1)
    const rank = classification.confidence + Math.min(0.04, retainedRatio * 0.04)
    if (!glyphCandidate || rank > glyphCandidate.rank) {
      glyphCandidate = { startIndex, classification, rank }
    }
  }
  if (!glyphCandidate) return rejected('看不清中央符號，請完成整筆後再放開', { outer })

  const { startIndex: innerStartIndex, classification } = glyphCandidate
  return {
    ok: true,
    reason: '整筆咒式成立',
    outer,
    classification,
    outerEndIndex,
    innerStartIndex,
    outerPoints: points.slice(0, outerEndIndex),
    bridgePoints: points.slice(outerEndIndex, innerStartIndex),
    centralPoints: points.slice(innerStartIndex),
    rawPoints: [...points],
  }
}

export function createSigilCastMachine({
  mode = 'camera', fiveFramesRequired = 4, viewportWidth = 390, viewportHeight = 844,
  centralZoneRatio = 0.72, landmarkGraceMs = 1200,
} = {}) {
  const initialStage = mode === 'camera' ? 'awaitingFive' : 'ready'
  const initialState = () => ({
    mode,
    stage: initialStage,
    fiveFrames: 0,
    drawing: false,
    strokeEnded: false,
    rawPoints: [],
    outer: null,
    outerPoints: [],
    bridgePoints: [],
    centralPoints: [],
    classification: null,
    element: null,
    castCount: 0,
    landmarkPaused: false,
    completionFistFrames: 0,
    completionOpenFrames: 0,
    failureReason: null,
  })
  let state = initialState()
  const reset = (failureReason = null) => {
    state = initialState()
    state.failureReason = failureReason
  }
  const finalizeWholeStroke = () => {
    const analysis = analyzeWholeStroke(state.rawPoints, { viewportWidth, viewportHeight, centralZoneRatio })
    state.drawing = false
    state.strokeEnded = true
    state.classification = analysis.classification
    if (!analysis.ok) {
      state.failureReason = analysis.reason
      return false
    }
    state.outer = analysis.outer
    state.outerPoints = analysis.outerPoints
    state.bridgePoints = analysis.bridgePoints
    state.centralPoints = analysis.centralPoints
    state.element = analysis.classification.element
    state.stage = 'beautifying'
    return true
  }
  return {
    getState: () => structuredClone(state),
    send(event, payload = {}) {
      if (event === 'RETRY') { reset(); return this.getState() }
      if (event === 'LANDMARK_LOST' && state.drawing) {
        if ((payload.elapsedMs ?? 0) <= landmarkGraceMs) state.landmarkPaused = true
        else if (!finalizeWholeStroke()) reset('landmark-timeout')
        return this.getState()
      }
      if (event === 'LANDMARK_RESUMED') {
        state.landmarkPaused = false
        return this.getState()
      }
      if (state.stage === 'drawing' && event === 'CAST_FIST') {
        state.completionFistFrames += 1
        if (state.completionFistFrames >= 2) {
          state.drawing = false
          state.landmarkPaused = false
          state.completionOpenFrames = 0
          state.stage = 'awaitingCastOpen'
        }
        return this.getState()
      }
      if (state.stage === 'drawing' && event === 'CAST_FIST_LOST') {
        state.completionFistFrames = 0
        return this.getState()
      }
      if (state.stage === 'awaitingCastOpen' && event === 'CAST_OPEN') {
        state.completionOpenFrames += 1
        if (state.completionOpenFrames >= 2 && !finalizeWholeStroke()) reset(state.failureReason || 'whole-stroke-rejected')
        return this.getState()
      }
      if (state.stage === 'awaitingCastOpen' && event === 'CAST_OPEN_LOST') {
        state.completionOpenFrames = 0
        return this.getState()
      }
      if (state.stage === 'awaitingCastOpen' && event === 'CAST_RESUME') {
        state.stage = 'drawing'
        state.drawing = true
        state.completionFistFrames = 0
        state.completionOpenFrames = 0
        return this.getState()
      }
      if (state.stage === 'awaitingFive' && event === 'FIVE') {
        state.fiveFrames += 1
        if (state.fiveFrames >= fiveFramesRequired) state.stage = 'ready'
      } else if (state.stage === 'awaitingFive' && event === 'FIVE_LOST') {
        state.fiveFrames = 0
      } else if (state.stage === 'ready' && event === 'STROKE_START') {
        state.drawing = true
        state.strokeEnded = false
        state.rawPoints = []
        state.failureReason = null
        state.stage = 'drawing'
      } else if (state.stage === 'drawing' && event === 'STROKE_POINTS' && state.drawing) {
        state.rawPoints.push(...structuredClone(payload.points ?? []))
      } else if (state.stage === 'drawing' && event === 'STROKE_END') {
        if (!finalizeWholeStroke()) reset(state.failureReason || 'whole-stroke-rejected')
      } else if (state.stage === 'beautifying' && event === 'BEAUTIFY_DONE') {
        state.stage = 'casting'
        state.castCount += 1
      } else if (state.stage === 'casting' && event === 'CASTING_DONE') {
        state.stage = 'result'
      }
      return this.getState()
    },
  }
}

function interpolate(vertices, samplesPerEdge = 12, close = false) {
  const points = []
  const edges = close ? vertices.length : vertices.length - 1
  for (let edge = 0; edge < edges; edge += 1) {
    const start = vertices[edge]; const end = vertices[(edge + 1) % vertices.length]
    for (let index = 0; index < samplesPerEdge; index += 1) {
      const t = index / samplesPerEdge
      points.push({ x: start.x + (end.x - start.x) * t, y: start.y + (end.y - start.y) * t })
    }
  }
  points.push(close ? { ...vertices[0] } : { ...vertices.at(-1) })
  return points
}

export function makeSigilFixture(shape) {
  if (shape === 'triangle') return interpolate([{ x: 100, y: 20 }, { x: 25, y: 150 }, { x: 175, y: 150 }], 14, true)
  if (shape === 'square') return interpolate([{ x: 30, y: 30 }, { x: 170, y: 30 }, { x: 170, y: 170 }, { x: 30, y: 170 }], 12, true)
  if (shape === 'wave') return Array.from({ length: 64 }, (_, index) => ({ x: 15 + index * 3.2, y: 100 + Math.sin(index / 5) * 24 }))
  if (shape === 'spiral') return Array.from({ length: 90 }, (_, index) => {
    const angle = index / 89 * Math.PI * 5
    const radius = 5 + index * 0.92
    return { x: 100 + Math.cos(angle) * radius, y: 100 + Math.sin(angle) * radius }
  })
  if (shape === 'heart') return interpolate([
    { x: 100, y: 58 }, { x: 82, y: 30 }, { x: 52, y: 22 }, { x: 26, y: 45 }, { x: 30, y: 82 },
    { x: 52, y: 112 }, { x: 100, y: 170 }, { x: 148, y: 112 }, { x: 170, y: 82 }, { x: 174, y: 45 }, { x: 148, y: 22 }, { x: 118, y: 30 },
  ], 7, true)
  if (shape === 'vee') return interpolate([{ x: 30, y: 35 }, { x: 100, y: 160 }, { x: 170, y: 35 }], 18, false)
  if (shape === 'line') return Array.from({ length: 40 }, (_, index) => ({ x: 20 + index * 4, y: 80 + index * 0.8 }))
  if (shape === 'circle') return Array.from({ length: 65 }, (_, index) => ({ x: 100 + Math.cos(index / 64 * Math.PI * 2) * 70, y: 100 + Math.sin(index / 64 * Math.PI * 2) * 70 }))
  if (shape === 'openArc') return Array.from({ length: 50 }, (_, index) => ({ x: 100 + Math.cos(index / 64 * Math.PI * 1.55) * 70, y: 100 + Math.sin(index / 64 * Math.PI * 1.55) * 70 }))
  if (shape === 'scribble') return [{ x: 20, y: 20 }, { x: 170, y: 155 }, { x: 35, y: 145 }, { x: 165, y: 35 }, { x: 60, y: 25 }, { x: 145, y: 160 }, { x: 25, y: 85 }, { x: 175, y: 90 }, { x: 70, y: 165 }, { x: 115, y: 20 }, { x: 20, y: 20 }]
  return []
}

export function makeOneStrokeFixture(shape, {
  center = { x: 195, y: 390 }, outerRadius = 112, innerScale = 0.7,
} = {}) {
  const outer = Array.from({ length: 65 }, (_, index) => {
    const angle = index / 64 * Math.PI * 2
    return { x: center.x + Math.cos(angle) * outerRadius, y: center.y + Math.sin(angle) * outerRadius }
  })
  const inner = makeSigilFixture(shape).map((point) => ({
    x: center.x + (point.x - 100) * innerScale,
    y: center.y + (point.y - 100) * innerScale,
  }))
  const bridgeStart = outer.at(-1)
  const bridgeEnd = inner[0]
  const bridge = Array.from({ length: 8 }, (_, index) => {
    const t = (index + 1) / 9
    return {
      x: bridgeStart.x + (bridgeEnd.x - bridgeStart.x) * t,
      y: bridgeStart.y + (bridgeEnd.y - bridgeStart.y) * t,
    }
  })
  return {
    outer,
    bridge,
    inner,
    points: [...outer, ...bridge, ...inner],
    outerEndIndex: outer.length - 1,
    innerStartIndex: outer.length + bridge.length,
  }
}
